import { useLingui } from '@lingui/react/macro';
import { LockKeyhole } from 'lucide-react';
import { Fragment, type CSSProperties, type ReactNode } from 'react';

import { BrandLogo, type Brand } from '@/shared/brand';
import { shortcutLabel } from '@/shared/commands';
import { cn } from '@/shared/ui/cn';

import { LuyMark } from '../shell/luy/luy-mark';


// Palco de marca do login (desktop) e a faixa condensada do iPhone. Cor só por token: o fundo é o
// `stage` do Lume e a única luz, o accent do tenant (U16).

const delay = (ms: number): CSSProperties => ({ '--uyzy-delay': `${String(ms)}ms` }) as CSSProperties;

/** A prova de confiança numa linha só: conexão, segundo fator (ou acesso por perfil sem `USE_MFA`) e LGPD. */
function useTrust(mfa: boolean): readonly string[] {
  const { t } = useLingui();
  return [t`Conexão criptografada`, mfa ? t`Autenticação em dois fatores` : t`Acesso por perfil`, 'LGPD'];
}

function TrustLine({ mfa, className }: { mfa: boolean; className?: string }) {
  const { t } = useLingui();
  const items = useTrust(mfa);
  return (
    <p data-slot="trust-line" aria-label={t`Segurança`} className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      <LockKeyhole aria-hidden className="size-3.5 shrink-0" strokeWidth={1.75} />
      {items.map((item, index) => (
        <Fragment key={item}>
          {index > 0 ? <span aria-hidden>·</span> : null}
          <span>{item}</span>
        </Fragment>
      ))}
    </p>
  );
}

function Wordmark({ name, className }: { name: string; className?: string }) {
  return <span className={cn('text-xl font-semibold tracking-tight', className)}>{name}</span>;
}


/** UYZY é o produto (título, em display espaçado); a logo do domínio (UY3 ou o corban) vem ao lado, menor. */
function ProductLockup({ brand, size }: { brand: Brand | undefined; size: 'lg' | 'sm' }) {
  const large = size === 'lg';
  return (
    <div className="flex min-w-0 items-center gap-3.5">
      <p
        data-slot="product-wordmark"
        className={cn(
          'shrink-0 leading-none font-semibold text-stage-foreground',
          large ? '-mr-[0.32em] text-[30px] tracking-[0.32em]' : '-mr-[0.28em] text-[22px] tracking-[0.28em]',
        )}
      >
        UY<span className="uyzy-stage-highlight">ZY</span>
      </p>
      {brand ? (
        <>
          <span aria-hidden className={cn('ml-[0.4em] w-px shrink-0 bg-stage-line', large ? 'h-8' : 'h-6')} />
          <BrandLogo
            brand={brand}
            variant="full"
            surface="stage"
            className={cn('min-w-0', large ? 'h-7 max-w-[140px]' : 'h-6 max-w-[110px]')}
            fallback={<Wordmark name={brand.name} className={cn('text-stage-muted', large ? 'text-base' : 'text-sm')} />}
          />
        </>
      ) : null}
    </div>
  );
}

/** O ciclo real de uma operação de crédito, com as etapas do stepper do detalhe da Operação (`CREDIT_STEPS`). */
function useCycle() {
  const { t } = useLingui();
  return [
    { id: 'draft', label: t`Rascunho` },
    { id: 'approvals', label: t`Aprovações` },
    { id: 'signature', label: t`Assinatura` },
    { id: 'liquidation', label: t`Liquidação` },
    { id: 'finished', label: t`Encerrado` },
  ] as const;
}

/** Etapa em destaque na linha do tempo: a assinatura, o meio do caminho entre a proposta e o dinheiro. */
const CYCLE_FOCUS = 2;

/**
 * A espinha do produto: a linha do tempo de uma operação na linguagem do `StatusStepper` (linha fina, nós
 * circulares, rótulos em DM Sans), estática, com um nó só no accent.
 */
function CreditCycle() {
  const { t } = useLingui();
  const steps = useCycle();
  return (
    <figure data-slot="credit-cycle" className="flex max-w-[30rem] flex-col gap-4">
      <ol aria-label={t`Ciclo de uma operação de crédito`} className="flex items-start">
        {steps.map((step, index) => {
          const focus = index === CYCLE_FOCUS;
          const last = index === steps.length - 1;
          return (
            <li key={step.id} data-step={step.id} className={cn('flex min-w-0 flex-col gap-2.5', last ? 'flex-none' : 'flex-1')} {...(focus ? { 'aria-current': 'step' as const } : {})}>
              <span aria-hidden className="flex h-[18px] items-center">
                {focus ? (
                  <span className="uyzy-cycle-focus inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border-2">
                    <span className="size-1.5 rounded-full bg-current" />
                  </span>
                ) : (
                  <span className="size-2.5 shrink-0 rounded-full border border-stage-muted bg-stage" />
                )}
                {last ? null : <span className="mx-2 h-px flex-1 bg-stage-line" />}
              </span>
              <span className={cn('text-[13px] leading-5 whitespace-nowrap', focus ? 'font-medium text-stage-foreground' : 'text-stage-muted')}>{step.label}</span>
            </li>
          );
        })}
      </ol>
      <figcaption className="max-w-[26rem] text-sm leading-6 text-stage-foreground/85">{t`Da proposta ao encerramento, cada etapa com dono, prova e histórico.`}</figcaption>
    </figure>
  );
}

/** As frentes do produto em linha editorial, com o ponto médio no accent. */
function ProductLine() {
  const { t } = useLingui();
  const areas = [t`Crédito`, t`Cobrança`, t`Contas digitais`];
  return (
    <ul data-slot="product-line" aria-label={t`Frentes do UYZY`} className="flex flex-wrap items-baseline gap-x-3 text-[17px] leading-7 font-medium tracking-[-0.01em] text-stage-foreground">
      {areas.map((area, index) => (
        <li key={area} className="inline-flex items-baseline gap-3">
          {index > 0 ? (
            <span aria-hidden className="uyzy-stage-highlight">
              ·
            </span>
          ) : null}
          {area}
        </li>
      ))}
    </ul>
  );
}

/** A assinatura do Luy: o mascote real (a única face da tela), pequeno, e uma frase na voz dele. */
function LuySignature() {
  const { t } = useLingui();
  const shortcut = shortcutLabel('j');
  return (
    <div data-slot="luy-signature" className="uyzy-stage-signature flex max-w-[26rem] items-center gap-3 rounded-2xl border border-stage-line py-2.5 pr-4 pl-2.5">
      <LuyMark className="size-10" animated={false} />
      <p className="text-[13px] leading-5 text-stage-muted">
        <span className="font-medium text-stage-foreground">{t`Oi, eu sou o Luy.`}</span> {t`Depois de entrar, é só me chamar com ${shortcut}.`}
      </p>
    </div>
  );
}

/**
 * Painel de marca do login no desktop ("autêntico", 06/10/2026): lockup no topo; sobretítulo, título
 * editorial, subtítulo, o ciclo real da operação, as frentes do produto e a assinatura do Luy, alinhados à
 * esquerda; a confiança numa linha no rodapé. Atrás, as fibras (`auth-fibers.tsx`); aqui nada se move além
 * da entrada em escada (`uyzy-rise`). Versões anteriores: `docs/design/login-orbita-v1` e o histórico do git.
 */
export function AuthStage({ brand, mfa }: { brand: Brand | undefined; mfa: boolean }) {
  const { t } = useLingui();
  const name = brand?.name ?? 'UYZY';
  return (
    <aside aria-label={t`Sobre o ${name}`} className="uyzy-stage relative hidden h-dvh flex-col self-start overflow-hidden p-10 lg:sticky lg:top-0 lg:flex xl:p-14">
      <div className="uyzy-rise relative">
        <ProductLockup brand={brand} size="lg" />
      </div>

      <div className="relative flex max-w-[46rem] flex-1 flex-col justify-center gap-12 py-10">
        <div style={delay(80)} className="uyzy-rise flex flex-col gap-6">
          <p className="text-[11px] font-semibold tracking-[0.2em] text-stage-muted uppercase">{t`Backoffice de crédito e banking`}</p>
          <p className="text-[2.75rem] leading-[1.04] font-semibold tracking-[-0.04em] text-stage-foreground xl:text-[3.5rem] 2xl:text-[4rem]">
            <span className="block xl:whitespace-nowrap">{t`Todo o ciclo do crédito,`}</span>
            <span className="uyzy-stage-highlight block xl:whitespace-nowrap">{t`em um só lugar.`}</span>
          </p>
          <p className="max-w-[28rem] text-[17px] leading-7 font-normal text-stage-muted">{t`Origine, formalize, cobre e movimente com segurança e rastreabilidade de ponta a ponta.`}</p>
        </div>

        <div style={delay(180)} className="uyzy-rise flex flex-col gap-8">
          <CreditCycle />
          <ProductLine />
        </div>

        <div style={delay(280)} className="uyzy-rise">
          <LuySignature />
        </div>
      </div>

      <div style={delay(360)} className="uyzy-rise relative max-w-[40rem] border-t border-stage-line pt-5">
        <TrustLine mfa={mfa} className="text-xs text-stage-muted" />
      </div>
    </aside>
  );
}

/** A marca condensada do iPhone e da tela estreita: faixa do palco com o lockup e a linha de valor. */
export function AuthBrandBand({ brand }: { brand: Brand | undefined }) {
  const { t } = useLingui();
  return (
    <div className="uyzy-stage uyzy-rise relative flex items-center justify-between gap-3 overflow-hidden rounded-2xl px-4 py-3.5 lg:hidden">
      <div className="relative z-10 flex min-w-0 flex-col gap-2">
        <ProductLockup brand={brand} size="sm" />
        <p className="truncate text-xs text-stage-muted">{t`Backoffice de crédito e banking`}</p>
      </div>
    </div>
  );
}

/** Prova de confiança compacta, no rodapé da coluna do formulário (fora do desktop largo). */
export function AuthTrustRow({ mfa, children }: { mfa: boolean; children?: ReactNode }) {
  return (
    <footer className="flex flex-col items-center gap-3 text-xs text-muted-foreground">
      <TrustLine mfa={mfa} className="justify-center lg:hidden" />
      {children}
    </footer>
  );
}
